/**
 * P3 Enterprise primitives.
 *
 * Every surface in this repo is built from these. They encode the rules that
 * the design language spec says must not be re-litigated per screen:
 *
 *   · ONE CARD ANATOMY. Header, at most one lead figure, one body block, one
 *     footnote. Variants change the body, never the frame.
 *   · NO RAW NUMBERS. Figures arrive as `Cell<T>` from src/lib/suppression.ts
 *     and render through `CellValue`. A suppressed cell is a DESIGNED state,
 *     not an error state, and it reads differently from an unavailable one,
 *     because a customer who confuses the two thinks the product is broken.
 *   · EVERY FIGURE CARRIES A DENOMINATOR AND A SOURCE. `Denominator` renders
 *     the first; `ProvenanceNote` and `Trace` render the second. A card whose
 *     metric object cannot fill its provenance slot does not ship.
 *   · NUMBERS DO NOT ANIMATE. Nothing in this file transitions a value.
 *
 * Styling lives in src/app/styles/base.css, section 6. Class names are
 * namespaced `e-`.
 */

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "~/app/lib/cn";
import {
  DIRECTIONAL_DENOMINATOR,
  INTERVAL_DENOMINATOR,
  adjustedWaldInterval,
  formatCount,
  formatInterval,
  formatMore,
  formatPercent,
  pluralize,
} from "~/app/lib/format";
import type { Cell, Rate, SuppressionReason } from "~/lib/suppression";

/* ==========================================================================
   Shared vocabulary
   ========================================================================== */

/** Semantic tone. Never used to encode a category; categorical colour never
 *  encodes valence. The two palettes do not appear in the same mark. */
export type Tone = "neutral" | "good" | "warn" | "crit" | "accent";

/** The direction of a change. Drives the delta pill and the KPI accent tile,
 *  so a red dot never appears without a falling number behind it. */
export type Direction = "up" | "down" | "flat";

/**
 * The metric object behind every figure on this surface.
 *
 * This is the contract the data layer fills and the Trace drawer renders, in
 * this field order. It is also the CSV export's column set and the Board
 * Brief's source-appendix row, so there is ONE definition of a metric in the
 * codebase and three renderings of it.
 *
 * A card whose metric cannot fill fields 1 through 6 does not render. That is
 * the mechanism that stops an unsourced number reaching a customer.
 */
export interface Provenance {
  /** 1. One plain sentence. The same string appears in every export. */
  readonly definition: string;
  /** 2. The named system and read path. "From in-app activity on P3." */
  readonly source: string;
  /** 3. The window, both ends stated. "Aug 4 to Sep 1, 2026". */
  readonly window: string;
  /** 3b. The anchoring rule, when one applies. "Weeks end on Sunday, so a
   *  partial week never reads as a collapse." */
  readonly windowRule?: string;
  /** 4. What this figure is a share of, and what is excluded. */
  readonly denominator: string;
  /** 5. Present only when something on this card is withheld. "2 groups are
   *  not shown." The legend sentence is appended automatically. */
  readonly suppression?: string;
  /** 6. When the figure was computed, and how often it is recomputed. */
  readonly freshness: { readonly asOf: string; readonly cadence: string };
  /** 7. Plain-language caveats. This is where an honest system says the count
   *  is a floor rather than a roster. */
  readonly limits?: readonly string[];
}

/* ==========================================================================
   Suppression copy. Verbatim, and it is not apologetic: the sentence turns a
   constraint into a credential, and it is the correct answer to a data
   governance officer.
   ========================================================================== */

export const SUPPRESSED_MARKER = "n<10";

export const SUPPRESSION_LEGEND =
  "Groups under 10 students are not shown, the threshold state education reporting uses.";

export const SUPPRESSION_TOOLTIP =
  "Groups under 10 students are combined, to protect student privacy.";

export const SUPPRESSED_ARIA = "not shown, fewer than 10 students";

const SUPPRESSION_WHY: Record<SuppressionReason, string> = {
  "below-threshold": "This figure covers fewer than 10 students.",
  complementary:
    "This figure is held back so a smaller figure on this card cannot be worked out by subtracting the ones shown.",
  "remainder-pinned":
    "This figure is held back because the groups behind it could be worked out from the total.",
};

/**
 * Turns the machine reason on an `unavailable` cell into a sentence a
 * university administrator can act on. Unavailable is not suppressed: this
 * copy says P3 does not have the figure, not that P3 is withholding it.
 */
export function describeUnavailable(reason: string): string {
  switch (reason) {
    case "not a valid count":
    case "not a valid rate":
      return "P3 did not receive a usable number for this figure.";
    case "no denominator":
      return "There is no group to measure this against yet.";
    case "no data":
      return "P3 has not received any data for this figure yet.";
    case "nothing suppressed":
      return "Nothing on this card is held back.";
    default:
      return reason.charAt(0).toUpperCase() + reason.slice(1) + ".";
  }
}

/* ==========================================================================
   Accessibility helpers
   ========================================================================== */

export function SrOnly({ children }: { children: ReactNode }) {
  return <span className="e-sr-only">{children}</span>;
}

/**
 * The text fallback that sits beside every chart. WCAG 2.1 AA is a
 * procurement gate with a date on it, and a chart with no readable data is
 * the single most common finding in a higher-education accessibility audit.
 *
 * Cells are strings, already formatted, because this is the same data a
 * screen reader and a grayscale printer see.
 */
export function VisuallyHiddenTable({
  caption,
  columns,
  rows,
}: {
  caption: string;
  columns: readonly string[];
  rows: readonly (readonly string[])[];
}) {
  return (
    <table className="e-sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c} scope="col">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional data, not identities
          <tr key={i}>
            {row.map((cell, j) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: same
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ==========================================================================
   CellValue: the most important component on the surface

   Three states, three appearances, three accessible names. A suppressed cell
   reads calmly and explains itself; it never looks broken. The marker is the
   literal string n<10: not a dash, not blank, not "N/A", not a padlock.
   ========================================================================== */

export interface CellValueProps<T> {
  cell: Cell<T>;
  /** Renders the value. In practice one of the format.ts helpers. */
  format: (value: T) => ReactNode;
  /** Names this figure for assistive tech, so a suppressed cell announces as
   *  "Active students: not shown, fewer than 10 students" rather than as a
   *  bare marker. Required on purpose. */
  label: string;
  className?: string;
  /** Extra sentence for the unavailable state, when the surface knows more
   *  than the data layer's reason string does. */
  unavailableNote?: string;
}

/**
 * WCAG 1.4.13 for the cell explanations (accessibility audit E16, Sep 25 2026).
 * Hover or keyboard focus opens one; Escape hides it without moving either,
 * and it stays hidden until both have left the cell. The key listener sits on
 * the document in the capture phase and acts only while the explanation is
 * actually on screen, so the first Escape closes the explanation and the next
 * one still reaches the drawer it sits in (Trace listens in the bubble phase).
 * The CSS half, a bridge over the gap so the pointer can reach the box, is in
 * base.css beside `.e-cell-why`.
 */
function useDismissibleWhy() {
  const whyRef = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const active = hovered || focused;

  useEffect(() => {
    if (!active) {
      setDismissed(false);
      return;
    }
    if (dismissed) return;
    const onKey = (event: KeyboardEvent) => {
      const why = whyRef.current;
      if (event.key !== "Escape" || !why || getComputedStyle(why).display === "none") return;
      event.stopPropagation();
      setDismissed(true);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [active, dismissed]);

  return {
    whyRef,
    cellProps: {
      "data-why-dismissed": dismissed ? "" : undefined,
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    },
  };
}

export function CellValue<T>({
  cell,
  format,
  label,
  className,
  unavailableNote,
}: CellValueProps<T>) {
  const whyId = useId();
  const { whyRef, cellProps } = useDismissibleWhy();

  if (cell.kind === "value") {
    return <span className={cn("e-num", className)}>{format(cell.value)}</span>;
  }

  if (cell.kind === "suppressed") {
    return (
      <span
        className={cn("e-cell-suppressed", className)}
        tabIndex={0}
        role="note"
        aria-label={`${label}: ${SUPPRESSED_ARIA}`}
        aria-describedby={whyId}
        {...cellProps}
      >
        {SUPPRESSED_MARKER}
        <span className="e-cell-why" id={whyId} ref={whyRef}>
          {SUPPRESSION_WHY[cell.reason]} {SUPPRESSION_LEGEND}
        </span>
      </span>
    );
  }

  const note = unavailableNote ?? describeUnavailable(cell.reason);
  return (
    <span
      className={cn("e-cell-unavailable", className)}
      tabIndex={0}
      role="note"
      aria-label={`${label}: not available. ${note}`}
      aria-describedby={whyId}
      {...cellProps}
    >
      Not available
      <span className="e-cell-why" id={whyId} ref={whyRef}>
        {note}
      </span>
    </span>
  );
}

/** The verbatim legend. Renders in the footnote of any card holding a
 *  suppressed cell, and on every printed page containing one. */
export function SuppressionLegend({ className }: { className?: string }) {
  return <p className={cn("e-note e-legend", className)}>{SUPPRESSION_LEGEND}</p>;
}

/* ==========================================================================
   Figures
   ========================================================================== */

export interface LeadFigureProps<T> {
  cell: Cell<T>;
  format: (value: T) => ReactNode;
  /** The figure's name, for assistive tech and for the Trace drawer's title. */
  label: string;
  /** When present the figure becomes a Trace target: it looks like a number
   *  until you approach it. */
  provenance?: Provenance;
  className?: string;
  unavailableNote?: string;
}

/**
 * The single big number in a card. AT MOST ONE PER CARD; every other figure
 * on the card is a `Minor`, and no figure appears twice on one card.
 */
export function LeadFigure<T>({
  cell,
  format,
  label,
  provenance,
  className,
  unavailableNote,
}: LeadFigureProps<T>) {
  const figure = (
    <CellValue
      cell={cell}
      format={format}
      label={label}
      unavailableNote={unavailableNote}
    />
  );
  return (
    <p className={cn("e-figure-lead e-kpi-value", className)}>
      {provenance ? (
        <Trace provenance={provenance} label={label}>
          {figure}
        </Trace>
      ) : (
        figure
      )}
    </p>
  );
}

export interface MinorProps<T> {
  cell: Cell<T>;
  format: (value: T) => ReactNode;
  label: string;
  className?: string;
  unavailableNote?: string;
}

/** A secondary figure: 20px, label above. Everything on a card that is not
 *  the lead figure is one of these. */
export function Minor<T>({
  cell,
  format,
  label,
  className,
  unavailableNote,
}: MinorProps<T>) {
  return (
    <div className={cn("e-minor", className)}>
      <p className="e-label">{label}</p>
      <p className="e-figure" style={{ marginTop: "var(--sp-2)" }}>
        <CellValue
          cell={cell}
          format={format}
          label={label}
          unavailableNote={unavailableNote}
        />
      </p>
    </div>
  );
}

export interface DenominatorProps {
  cell: Cell<Rate>;
  /** The clause that completes the first line, without the percentage:
   *  "of matched students completed a milestone this term". */
  of: string;
  /** The noun for the second line: "matched students". */
  noun: string;
  /** The figure's name for assistive tech. */
  label: string;
  className?: string;
}

/**
 * A rate, published with its parts. THE MANDATORY FORMAT, used in KPI tiles,
 * insights, table cells and chart captions alike:
 *
 *   68% of matched students completed a milestone this term
 *   17 of 25 matched students · 95% CI 49 to 83
 *
 * A percentage without its denominator does not ship. Under 100 the interval
 * renders; under 30 the figure also carries a Directional chip, because on a
 * 25-person cohort a rate is a direction, not a measurement.
 */
export function Denominator({ cell, of, noun, label, className }: DenominatorProps) {
  if (cell.kind !== "value") {
    return (
      <div className={cn("e-rate", className)}>
        <p className="e-rate-line1">
          <CellValue cell={cell} format={() => null} label={label} />
          <span className="e-rate-of">{of}</span>
        </p>
      </div>
    );
  }

  const { numerator, denominator, rate } = cell.value;
  const interval =
    denominator < INTERVAL_DENOMINATOR
      ? adjustedWaldInterval(numerator, denominator)
      : null;
  const directional = denominator < DIRECTIONAL_DENOMINATOR;

  return (
    <div className={cn("e-rate", className)}>
      <p className="e-rate-line1">
        <span className="e-figure">{formatPercent(rate)}</span>
        <span className="e-rate-of">{of}</span>
        {directional ? <Chip tone="warn">Directional</Chip> : null}
      </p>
      <p className="e-rate-line2">
        {formatCount(numerator)} of {formatCount(denominator)} {noun}
        {interval ? (
          <>
            <span className="e-rate-sep" aria-hidden="true">
              ·
            </span>
            {formatInterval(interval)}
          </>
        ) : null}
      </p>
    </div>
  );
}

export interface DeltaPillProps {
  direction: Direction;
  /** The change itself: "+18%". Formatted by formatPercentChange. */
  value: string;
  /** The comparison period. REQUIRED: "+18%" alone is not shippable,
   *  "+18% year over year" is. The visible note on the card repeats it; this
   *  prop guarantees the accessible name always carries it. */
  comparison: string;
  className?: string;
}

export function DeltaPill({ direction, value, comparison, className }: DeltaPillProps) {
  const glyph = direction === "up" ? "▲" : direction === "down" ? "▼" : "";
  return (
    <span
      className={cn("e-delta", `dir-${direction}`, className)}
      aria-label={`${value} ${comparison}`}
    >
      {glyph ? <span aria-hidden="true">{glyph}</span> : null}
      <span aria-hidden="true">{value}</span>
    </span>
  );
}

export interface ChipProps {
  tone?: Tone;
  children: ReactNode;
  /** Present only on a filter chip, and it must actually remove the filter.
   *  A span styled to look removable and wired to nothing is worse than no
   *  chip at all. */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}

export function Chip({
  tone = "neutral",
  children,
  onRemove,
  removeLabel,
  className,
}: ChipProps) {
  return (
    <span
      className={cn("e-chip", `tone-${tone}`, onRemove && "is-removable", className)}
    >
      {children}
      {onRemove ? (
        <button
          type="button"
          className="e-chip-x"
          onClick={onRemove}
          aria-label={removeLabel ?? "Remove filter"}
        >
          <span aria-hidden="true">{"×"}</span>
        </button>
      ) : null}
    </span>
  );
}

/* ==========================================================================
   Card
   ========================================================================== */

export type CardVariant = "plain" | "kpi" | "chart" | "table" | "ranked";

export interface CardProps extends Omit<ComponentProps<"section">, "title"> {
  variant?: CardVariant;
  /** 1, 2 or "full". Every grid row's spans must sum to three. */
  span?: 1 | 2 | "full";
  /** The single card of a 2+1 row. Takes the whole row at two columns rather
   *  than sitting beside a hole. */
  solo?: boolean;
  /** A chart card beside a list, so the two end level. Takes the SVG out of
   *  flow: in flow at height 100% it falls back to its viewBox ratio and
   *  outgrows the list on a wide monitor. */
  fill?: boolean;
  /** Plot height for a chart card. */
  plot?: "single" | "half" | "full" | "tier";
}

export function Card({
  variant = "plain",
  span = 1,
  solo,
  fill,
  plot = "single",
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <section
      className={cn(
        "e-card",
        variant !== "plain" && `is-${variant}`,
        span === 2 && "span-2",
        span === "full" && "span-full",
        solo && "is-solo",
        fill && "is-fill",
        variant === "chart" && plot !== "single" && `h-${plot}`,
        className,
      )}
      {...rest}
    >
      {children}
    </section>
  );
}

export interface CardHeaderProps {
  children: ReactNode;
  /** At most one right-aligned pill. */
  aside?: ReactNode;
  className?: string;
}

export function CardHeader({ children, aside, className }: CardHeaderProps) {
  return (
    <header className={cn(className)}>
      <div className="e-card-head-text">{children}</div>
      {aside ? <div className="e-card-head-aside">{aside}</div> : null}
    </header>
  );
}

export function CardTitle({
  children,
  sub,
  id,
  className,
}: {
  children: ReactNode;
  /** At most one sub line. */
  sub?: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <>
      <h3 id={id} className={cn("e-h3", className)}>
        {children}
      </h3>
      {sub ? <p className="e-card-sub">{sub}</p> : null}
    </>
  );
}

/** The one body block. A card does not hold a chart AND a table. */
export function CardBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("e-card-body", className)}>{children}</div>;
}

/**
 * The footnote slot. On this surface it is mandatory and it carries the
 * provenance line, so it is pinned to the bottom of the card and a row of
 * cards shares one footnote baseline.
 */
export function CardFootnote({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("e-footnote", className)}>{children}</div>;
}

/* ==========================================================================
   Provenance and Trace: the signature moment
   ========================================================================== */

/**
 * The one-line provenance footnote every card carries.
 *
 *   From in-app activity on P3, recomputed daily. 312 students claimed to
 *   Howard University.
 *
 * Pass `suppressed` when any cell on the card is withheld: the legend renders
 * beneath, verbatim.
 */
export function ProvenanceNote({
  provenance,
  suppressed,
  className,
}: {
  provenance: Provenance;
  suppressed?: boolean;
  className?: string;
}) {
  /* Sep 9 2026 (Thomas): "From P3 student records. Every student in view ..."
     under every card was repetitive. The provenance still lives in the Trace
     drawer on every figure and in Data and methods on Settings; here only the
     suppression legend survives, and only where a group was withheld. */
  void provenance;
  return suppressed ? <SuppressionLegend className={className} /> : null;
}

export interface TraceProps {
  provenance: Provenance;
  /** The figure's name. Titles the drawer. */
  label: string;
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a figure in a button that opens the provenance drawer. At rest it
 * looks exactly like the number: same type, same colour, no underline. A
 * dotted underline and a small glyph appear on hover and on focus.
 *
 * The drawer's fields are in a fixed order, and they are the same seven
 * fields that become CSV columns and Board Brief appendix rows.
 */
export function Trace({ provenance, label, children, className }: TraceProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    headingRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={cn("e-trace", className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {children}
        <svg
          className="e-trace-glyph"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="8" cy="8" r="6.25" />
          <path d="M8 7.2v3.6M8 5.1v.9" />
        </svg>
        <SrOnly>Where this number comes from</SrOnly>
      </button>
      {open ? (
        <TraceDrawer
          provenance={provenance}
          label={label}
          titleId={titleId}
          headingRef={headingRef}
          onClose={close}
        />
      ) : null}
    </>
  );
}

function TraceDrawer({
  provenance,
  label,
  titleId,
  headingRef,
  onClose,
}: {
  provenance: Provenance;
  label: string;
  titleId: string;
  headingRef: RefObject<HTMLHeadingElement | null>;
  onClose: () => void;
}) {
  if (typeof document === "undefined") return null;

  const body = (
    <>
      <button
        type="button"
        className="e-trace-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <aside
        className="e-trace-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="e-trace-head">
          <div>
            <p className="e-label">Where this number comes from</p>
            <h2
              id={titleId}
              ref={headingRef}
              tabIndex={-1}
              className="e-h2"
              style={{ marginTop: "var(--sp-2)" }}
            >
              {label}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <dl className="e-trace-body">
          <TraceField term="Definition">{provenance.definition}</TraceField>
          <TraceField term="Source">{provenance.source}</TraceField>
          <TraceField term="Window">
            {provenance.window}
            {provenance.windowRule ? (
              <>
                <br />
                {provenance.windowRule}
              </>
            ) : null}
          </TraceField>
          <TraceField term="Denominator">{provenance.denominator}</TraceField>
          <TraceField term="Suppression">
            {provenance.suppression
              ? `${provenance.suppression} ${SUPPRESSION_LEGEND}`
              : `Nothing on this figure is held back. ${SUPPRESSION_LEGEND}`}
          </TraceField>
          <TraceField term="Freshness">
            {provenance.freshness.asOf}
            <br />
            {provenance.freshness.cadence}
          </TraceField>
          {provenance.limits && provenance.limits.length > 0 ? (
            <TraceField term="Known limits">
              <ul className="e-trace-limits">
                {provenance.limits.map((limit) => (
                  <li key={limit}>{limit}</li>
                ))}
              </ul>
            </TraceField>
          ) : null}
        </dl>
      </aside>
    </>
  );

  return createPortal(body, document.body);
}

function TraceField({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="e-trace-field">
      <dt className="e-label">{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/* ==========================================================================
   Section furniture
   ========================================================================== */

export interface SectionHeadProps {
  /** Matches the rail's nav item for this section. THE SIDEBAR LABEL AND THE
   *  HEADING MUST BE THE SAME STRING: a disagreement there is exactly the
   *  seam a senior executive notices on first click. */
  id: string;
  title: ReactNode;
  eyebrow?: string;
  lede?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** "page" is the member app's page header (an h1, 22px, the faint 13px
   *  lede): the sub-pages wear it. The default is a section head inside a
   *  page, an h2 on the platform /admin's own section numbers. */
  level?: "section" | "page";
}

export function SectionHead({
  id,
  title,
  eyebrow,
  lede,
  actions,
  className,
  level = "section",
}: SectionHeadProps) {
  return (
    <header className={cn("e-shead", level === "page" && "is-page", className)}>
      <div className="e-shead-text">
        {eyebrow ? <p className="e-label e-shead-eyebrow">{eyebrow}</p> : null}
        {level === "page" ? (
          <h1 id={id} className="e-h1-page">
            {title}
          </h1>
        ) : (
          <h2 id={id} className="e-h2">
            {title}
          </h2>
        )}
        {lede ? <p className="e-shead-lede">{lede}</p> : null}
      </div>
      {actions ? <div className="e-shead-actions">{actions}</div> : null}
    </header>
  );
}

/** One accent word per heading, maximum. Crimson appears in six places on
 *  this surface and this is one of them. */
export function AccentWord({ children }: { children: ReactNode }) {
  return <span className="e-accent-word">{children}</span>;
}

/** The Ledger v4 within-section label: a small uppercase eyebrow that groups
 *  cards inside one section without promoting them to a section of their own. */
export function SubGroupLabel({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  /** Set it when a group beneath the label names itself with aria-labelledby,
   *  so assistive technology gets the grouping from the visible label rather
   *  than from a second copy of the same words in an aria-label. */
  id?: string;
}) {
  return (
    <p className={cn("e-cap e-label", className)} id={id}>
      {children}
    </p>
  );
}

/**
 * ONE PER SECTION, never one per card. It states a reading and its
 * denominator, and it never states a cause: "62% of students with a confirmed
 * mentor completed a milestone this term (17 of 27)" ships; anything with the
 * shape "X caused Y" does not.
 *
 * There are no colour variants. A section that needs two insights needs to be
 * two sections.
 */
export function Insight({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("e-insight", className)}>
      <svg
        className="e-insight-icon"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6.2 12.4h3.6M6.8 14.3h2.4" />
        <path d="M8 1.7a4.6 4.6 0 0 0-2.7 8.3v1.2h5.4v-1.2A4.6 4.6 0 0 0 8 1.7Z" />
      </svg>
      <p>{children}</p>
    </div>
  );
}

/** The filter bar. Controls in here are wired or they are absent. */
export function Toolbar({
  children,
  className,
  ...rest
}: ComponentProps<"div">) {
  return (
    <div className={cn("e-toolbar", className)} {...rest}>
      {children}
    </div>
  );
}

export function ToolbarSpacer() {
  return <div className="e-toolbar-spacer" />;
}

/** The "Detail for engineers" pattern: one disclosure per section, never a
 *  page of open technical prose. */
export function Disclosure({
  summary = "Detail for engineers",
  children,
  className,
  ...rest
}: ComponentProps<"details"> & { summary?: string }) {
  return (
    <details className={cn("e-disclosure", className)} {...rest}>
      <summary>{summary}</summary>
      <div className="e-disclosure-body">{children}</div>
    </details>
  );
}

/* ==========================================================================
   State cards

   Loading, empty, cohort-building, not-measured and down, in one component,
   so no screen hand-rolls its own. Ported from p3-web's query-states.tsx
   discipline, which exists because the same three affordances were
   copy-pasted across roughly twenty route files.

   Empty is a THIRD state, distinct from zero, on purpose: a zero reads as a
   measurement when the truth is "we are not collecting yet". None of these
   renders a 0, a dash, or an em dash.
   ========================================================================== */

export type StateVariant = "loading" | "empty" | "building" | "not-measured" | "down";

const STATE_DEFAULTS: Record<StateVariant, { title: string; body: string }> = {
  loading: { title: "Loading", body: "Fetching this panel." },
  empty: {
    title: "Nothing to show for these filters",
    body: "No students match the filters above. Clear a filter to widen the view.",
  },
  building: {
    title: "Your cohort is building",
    body: "P3 reports figures at 10 students or more, which is the same threshold used in state education reporting and a commitment in your Data Sharing Addendum.",
  },
  "not-measured": {
    title: "Not measured yet",
    body: "This begins once students start completing milestones in the app.",
  },
  down: {
    title: "This panel could not load",
    body: "The rest of the page is unaffected.",
  },
};

export interface StateCardProps {
  variant: StateVariant;
  /** Overrides the default heading. */
  title?: string;
  /** The specific sentence for this panel. Overrides the default body. */
  children?: ReactNode;
  /** One action, at most. "Invite students" / "Upload a roster". */
  action?: ReactNode;
  /** Cohort-building only: the progress toward the reporting floor. */
  progress?: { have: number; need: number; noun?: string };
  className?: string;
}

export function StateCard({
  variant,
  title,
  children,
  action,
  progress,
  className,
}: StateCardProps) {
  const defaults = STATE_DEFAULTS[variant];
  const isLoading = variant === "loading";
  const pct =
    progress && progress.need > 0
      ? Math.min(100, Math.round((progress.have / progress.need) * 100))
      : 0;

  return (
    <div
      className={cn("e-state", `is-${variant}`, className)}
      role={variant === "down" ? "alert" : undefined}
      aria-live={isLoading ? "polite" : undefined}
      aria-busy={isLoading ? true : undefined}
    >
      <p className="e-state-title">{title ?? defaults.title}</p>
      <p>{children ?? defaults.body}</p>
      {progress ? (
        <>
          <div
            className="e-state-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.need}
            aria-valuenow={progress.have}
            aria-label={`${formatCount(progress.have)} of ${formatCount(progress.need)} ${
              progress.noun ?? pluralize(progress.need, "student")
            }`}
          >
            <i style={{ width: `${pct}%` }} />
          </div>
          {/* The count is the point of the card, not a footnote (Sep 18 2026):
              on day one it is the one figure a new customer is watching. */}
          <p className="e-state-count">
            <b>{formatCount(progress.have)}</b> of {formatCount(progress.need)}{" "}
            {progress.noun ?? pluralize(progress.need, "student")}
          </p>
        </>
      ) : null}
      {action ? <div>{action}</div> : null}
    </div>
  );
}

/* ==========================================================================
   Buttons
   ========================================================================== */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

/** The class string, exported so an anchor (a download, a quote link) can
 *  wear the same button without this file growing a polymorphic `as` prop. */
export function buttonClass(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  className?: string,
): string {
  return cn("e-btn", `v-${variant}`, size !== "md" && `size-${size}`, className);
}

export interface ButtonProps extends ComponentProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({
  variant = "primary",
  size = "md",
  type = "button",
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} {...rest}>
      {children}
    </button>
  );
}

/* ==========================================================================
   Fields

   Field owns the id, the hint and the error, and passes them down through a
   context so a call site reads <Field label="Term"><Select …/></Field> with no
   id plumbing. Every control also works standalone.
   ========================================================================== */

interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
}

export function Field({ label, children, hint, error, className }: FieldProps) {
  const base = useId();
  const id = `${base}-control`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={cn("e-field", className)}>
        <label className="e-label e-field-label" htmlFor={id}>
          {label}
        </label>
        {children}
        {hint ? (
          <p className="e-field-hint" id={hintId}>
            {hint}
          </p>
        ) : null}
        {error ? (
          <p className="e-field-error" id={errorId}>
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export interface InputProps extends ComponentProps<"input"> {
  /** Right-aligns and switches to tabular figures. */
  numeric?: boolean;
}

export function Input({ numeric, className, ...rest }: InputProps) {
  const field = useContext(FieldContext);
  return (
    <input
      {...rest}
      id={rest.id ?? field?.id}
      aria-describedby={rest["aria-describedby"] ?? field?.describedBy}
      aria-invalid={rest["aria-invalid"] ?? (field?.invalid ? true : undefined)}
      className={cn("e-input", numeric && "is-num", className)}
    />
  );
}

export function Select({ className, children, ...rest }: ComponentProps<"select">) {
  const field = useContext(FieldContext);
  return (
    <select
      {...rest}
      id={rest.id ?? field?.id}
      aria-describedby={rest["aria-describedby"] ?? field?.describedBy}
      aria-invalid={rest["aria-invalid"] ?? (field?.invalid ? true : undefined)}
      className={cn("e-select", className)}
    >
      {children}
    </select>
  );
}

export interface CheckboxProps extends Omit<ComponentProps<"input">, "type"> {
  /** The label sits beside the box, not above it, so a consent line reads as
   *  a sentence. */
  label: ReactNode;
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  const field = useContext(FieldContext);
  const own = useId();
  const id = rest.id ?? field?.id ?? own;
  return (
    <label className={cn("e-checkbox", className)} htmlFor={id}>
      <input
        {...rest}
        type="checkbox"
        id={id}
        aria-describedby={rest["aria-describedby"] ?? field?.describedBy}
      />
      <span className="e-checkbox-text">{label}</span>
    </label>
  );
}

/* ==========================================================================
   Table
   ========================================================================== */

export function Table({ className, children, ...rest }: ComponentProps<"table">) {
  return (
    <table className={cn("e-table", className)} {...rest}>
      {children}
    </table>
  );
}

export function THead({ children, ...rest }: ComponentProps<"thead">) {
  return <thead {...rest}>{children}</thead>;
}

export function TBody({ children, ...rest }: ComponentProps<"tbody">) {
  return <tbody {...rest}>{children}</tbody>;
}

export function TR({ children, ...rest }: ComponentProps<"tr">) {
  return <tr {...rest}>{children}</tr>;
}

export interface THProps extends ComponentProps<"th"> {
  num?: boolean;
  /** Hidden below 600px in the wide table; still present in the stacked one. */
  secondary?: boolean;
}

export function TH({ num, secondary, className, children, ...rest }: THProps) {
  return (
    <th
      scope={rest.scope ?? "col"}
      className={cn(num && "num", secondary && "tbl-2nd", className)}
      {...rest}
    >
      {children}
    </th>
  );
}

export interface TDProps extends ComponentProps<"td"> {
  num?: boolean;
  secondary?: boolean;
}

export function TD({ num, secondary, className, children, ...rest }: TDProps) {
  return (
    <td className={cn(num && "num", secondary && "tbl-2nd", className)} {...rest}>
      {children}
    </td>
  );
}

/** The capped-list footer. The count is COMPUTED, never hardcoded. */
export function MoreRow({
  hiddenCount,
  noun,
  pluralNoun,
  colSpan,
}: {
  hiddenCount: number;
  noun: string;
  pluralNoun?: string;
  colSpan: number;
}) {
  if (hiddenCount <= 0) return null;
  return (
    <tr className="e-more-row">
      <td colSpan={colSpan}>{formatMore(hiddenCount, noun, pluralNoun)}</td>
    </tr>
  );
}

/** A column that is right-aligned only when every cell in it is a figure, the
 *  suppression marker, or an explicit absence. Mixed columns stay left. */
const NUMERIC_CELL = /^[~$]?\$?[\d,]+(\.\d+)?%?$/;

function isNumericColumn(values: readonly string[]): boolean {
  if (values.length === 0) return false;
  return values.every(
    (v) =>
      NUMERIC_CELL.test(v.trim()) ||
      v.trim() === SUPPRESSED_MARKER ||
      v.trim() === "Not available" ||
      v.trim() === "n/a",
  );
}

export interface TableColumn<R> {
  key: string;
  header: string;
  /** What renders in the cell. In practice a CellValue. */
  cell: (row: R) => ReactNode;
  /** The same cell as plain text: drives the auto-alignment rule, the phone
   *  reflow, and any text export. */
  text: (row: R) => string;
  /** Overrides the auto-alignment rule. */
  align?: "left" | "right";
  /** Dropped from the wide table below 600px. */
  secondary?: boolean;
}

export interface DataTableProps<R> {
  caption: string;
  columns: readonly TableColumn<R>[];
  rows: readonly R[];
  rowKey: (row: R) => string;
  /** Rows shown before the computed "and N more" line. There is no pagination
   *  control on a dashboard; the full set lives on the roster route. */
  cap?: number;
  moreNoun?: string;
  moreNounPlural?: string;
  /** The heading of each block in the phone reflow. Defaults to the first
   *  column's text. */
  stackTitle?: (row: R) => string;
  stackBadge?: (row: R) => ReactNode;
  className?: string;
}

/**
 * The table variant, with the four rules that keep it honest:
 * suppression is per cell rather than per row, so a row with two suppressed
 * cells still shows its other five; alignment is derived, not declared; the
 * list caps with a computed "and N more"; and BELOW 600px IT DOES NOT SCROLL
 * SIDEWAYS, it reflows into one block per row, because horizontal scroll
 * inside a card is a laptop affordance and a provost checking a number on a
 * phone should not have to swipe a table.
 */
export function DataTable<R>({
  caption,
  columns,
  rows,
  rowKey,
  cap = 12,
  moreNoun = "row",
  moreNounPlural,
  stackTitle,
  stackBadge,
  className,
}: DataTableProps<R>) {
  const shown = rows.slice(0, cap);
  const hidden = Math.max(0, rows.length - shown.length);

  const alignment = columns.map((column) => {
    if (column.align) return column.align;
    return isNumericColumn(shown.map((row) => column.text(row))) ? "right" : "left";
  });

  const firstColumn = columns[0];
  const titleOf = (row: R): string =>
    stackTitle ? stackTitle(row) : firstColumn ? firstColumn.text(row) : "";

  return (
    <div className={cn("e-table-wrap", className)}>
      <Table>
        <caption className="e-sr-only">{caption}</caption>
        <THead>
          <TR>
            {columns.map((column, i) => (
              <TH
                key={column.key}
                num={alignment[i] === "right"}
                secondary={column.secondary}
              >
                {column.header}
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {shown.map((row) => (
            <TR key={rowKey(row)}>
              {columns.map((column, i) => (
                <TD
                  key={column.key}
                  num={alignment[i] === "right"}
                  secondary={column.secondary}
                >
                  {column.cell(row)}
                </TD>
              ))}
            </TR>
          ))}
          <MoreRow
            hiddenCount={hidden}
            noun={moreNoun}
            pluralNoun={moreNounPlural}
            colSpan={columns.length}
          />
        </TBody>
      </Table>

      {/* The phone reflow. Same data, same order, no horizontal scroll. */}
      <div className="e-table-stack">
        {shown.map((row) => (
          <div className="e-stack-row" key={rowKey(row)}>
            <div className="e-stack-head">
              <p className="e-h3">{titleOf(row)}</p>
              {stackBadge ? stackBadge(row) : null}
            </div>
            <dl>
              {columns.slice(1).map((column) => (
                <div className="e-stack-line" key={column.key}>
                  <dt>{column.header}</dt>
                  <dd>{column.cell(row)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        {hidden > 0 ? (
          <p className="e-note">{formatMore(hidden, moreNoun, moreNounPlural)}</p>
        ) : null}
      </div>
    </div>
  );
}
